"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAccountSelection } from "@/contexts/account-selection-context";
import { useAccountStreamer } from "@/hooks/use-account-streamer";
import type { StreamerMessage } from "@/services/account-streamer";
import { apiClient } from "@/lib/api-client";
import type {
  BalanceRecord,
  BalanceStreamPayload,
  BalanceValue,
  NormalizedBalance,
} from "@/hooks/types";
import type { TastytradeBalance } from "@/types/tastytrade";

interface BalanceEntry {
  accountNumber: string;
  balance: TastytradeBalance | null;
  updatedAt: number | null;
  source: "stream" | "rest" | null;
  loading: boolean;
  error: string | null;
}

interface EnsureOptions {
  force?: boolean;
  staleMs?: number;
}

interface AccountBalancesContextValue {
  balances: Record<string, BalanceEntry>;
  getBalance: (accountNumber: string) => BalanceEntry | undefined;
  ensureBalance: (
    accountNumber: string,
    options?: EnsureOptions,
  ) => Promise<TastytradeBalance | null>;
  refreshBalance: (accountNumber: string) => Promise<TastytradeBalance | null>;
  isStreaming: boolean;
  lastStreamUpdate: number | null;
}

const AccountBalancesContext = createContext<
  AccountBalancesContextValue | undefined
>(undefined);

const DEFAULT_STALE_MS = 15000; // fallback to REST if no update within 15s

function normalizeBalanceValue(value: BalanceValue | undefined):
  | number
  | string
  | undefined {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const numeric = Number(trimmed);
    return Number.isNaN(numeric) ? trimmed : numeric;
  }
  if (value && typeof value === "object" && "value" in value) {
    const nested = (value as { value?: BalanceValue }).value;
    const normalized = normalizeBalanceValue(nested);
    if (normalized !== undefined) {
      return normalized;
    }
  }
  return undefined;
}

function normalizeBalanceRecord(
  balance: Record<string, unknown> | BalanceRecord | null | undefined,
): NormalizedBalance {
  const normalized: Record<string, number | string> = {};
  Object.entries(balance ?? {}).forEach(([key, value]) => {
    const normalizedValue = normalizeBalanceValue(value as BalanceValue | undefined);
    if (normalizedValue !== undefined) {
      normalized[key] = normalizedValue;
    }
  });
  return normalized;
}

function getRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function extractBalanceFromStream(data: unknown):
  | { accountNumber: string; balance: NormalizedBalance }
  | null {
  if (!data) return null;

  const root = getRecord(data);
  const payload: BalanceStreamPayload | null =
    (root?.data as BalanceStreamPayload | undefined) ?? (root as BalanceStreamPayload | null);

  if (!payload) {
    return null;
  }

  const accountNumber =
    payload["account-number"] ??
    payload.accountNumber ??
    payload.account?.["account-number"] ??
    payload.account?.accountNumber;

  if (!accountNumber) {
    return null;
  }

  const rawBalance =
    payload.balance ?? payload.balances ?? payload["account-balance"] ?? payload;

  const balanceSource = getRecord(rawBalance) as BalanceRecord | null;

  if (!balanceSource) {
    return null;
  }

  const balance = normalizeBalanceRecord(balanceSource);
  if (!Object.keys(balance).length) {
    return null;
  }

  return { accountNumber: String(accountNumber), balance };
}

function mergeBalance(
  current: TastytradeBalance | null,
  incoming: NormalizedBalance,
): TastytradeBalance | null {
  if (!current) {
    return normalizeBalanceRecord(incoming) as TastytradeBalance;
  }
  return {
    ...current,
    ...normalizeBalanceRecord(incoming),
  } as TastytradeBalance;
}

function resolveErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return fallback;
}

export function AccountBalancesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { selectedAccounts } = useAccountSelection();
  const {
    lastMessage,
    isConnected: streamerConnected,
  } = useAccountStreamer({ autoConnect: true });

  const [balancesByAccount, setBalancesByAccount] = useState<
    Record<string, BalanceEntry>
  >({});
  const [lastStreamUpdate, setLastStreamUpdate] = useState<number | null>(null);

  const entriesRef = useRef(balancesByAccount);
  useEffect(() => {
    entriesRef.current = balancesByAccount;
  }, [balancesByAccount]);

  const inFlight = useRef(new Map<string, Promise<TastytradeBalance | null>>());

  const upsertEntry = useCallback((accountNumber: string, entry: Partial<BalanceEntry>) => {
    setBalancesByAccount((prev) => {
      const existing = prev[accountNumber] ?? {
        accountNumber,
        balance: null,
        updatedAt: null,
        source: null,
        loading: false,
        error: null,
      };
      return {
        ...prev,
        [accountNumber]: {
          ...existing,
          ...entry,
        },
      };
    });
  }, []);

  const toStreamerMessage = useCallback((value: unknown): StreamerMessage | null => {
    if (!value || typeof value !== "object") {
      return null;
    }

    const candidate = value as Partial<StreamerMessage> & {
      data?: { type?: string; event?: string } | undefined;
    };

    if (typeof candidate.type !== "string") {
      return null;
    }

    return {
      type: candidate.type,
      data: candidate.data,
      timestamp: candidate.timestamp ?? new Date().toISOString(),
    };
  }, []);

  useEffect(() => {
    const message = toStreamerMessage(lastMessage);
    if (!message) {
      return;
    }

    const messageType =
      message.type ??
      (typeof message.data === "object"
        ? (message.data as { type?: string; event?: string })?.type ??
          (message.data as { event?: string })?.event
        : undefined);

    if (
      messageType !== "balance" &&
      messageType !== "balance-update" &&
      messageType !== "account-balance"
    ) {
      return;
    }

    const extracted = extractBalanceFromStream(message.data);
    if (!extracted) {
      return;
    }

    const { accountNumber, balance } = extracted;

    upsertEntry(accountNumber, {
      balance: mergeBalance(entriesRef.current[accountNumber]?.balance ?? null, balance),
      updatedAt: Date.now(),
      source: "stream",
      loading: false,
      error: null,
    });
    setLastStreamUpdate(Date.now());
  }, [lastMessage, toStreamerMessage, upsertEntry]);

  const fetchBalance = useCallback(
    async (accountNumber: string): Promise<TastytradeBalance | null> => {
      try {
        const response = await apiClient.fetchBalance(accountNumber);
        const normalized = normalizeBalanceRecord(
          response as Record<string, unknown>,
        );
        const now = Date.now();
        upsertEntry(accountNumber, {
          balance: normalized as TastytradeBalance,
          updatedAt: now,
          source: "rest",
          loading: false,
          error: null,
        });
        return normalized as TastytradeBalance;
      } catch (error: unknown) {
        upsertEntry(accountNumber, {
          loading: false,
          error: resolveErrorMessage(error, "Failed to fetch balance"),
        });
        return null;
      }
    },
    [upsertEntry],
  );

  const ensureBalance = useCallback(
    async (
      accountNumber: string,
      options: EnsureOptions = {},
    ): Promise<TastytradeBalance | null> => {
      const { force = false, staleMs = DEFAULT_STALE_MS } = options;
      const entry = entriesRef.current[accountNumber];
      const now = Date.now();

      if (!force && entry?.balance && entry.updatedAt) {
        const age = now - entry.updatedAt;
        if (age < staleMs) {
          return entry.balance;
        }
      }

      if (inFlight.current.has(accountNumber)) {
        return inFlight.current.get(accountNumber) ?? null;
      }

      upsertEntry(accountNumber, {
        loading: true,
        error: null,
      });

      const fetchPromise = fetchBalance(accountNumber);
      inFlight.current.set(accountNumber, fetchPromise);

      const result = await fetchPromise;
      inFlight.current.delete(accountNumber);
      return result;
    },
    [fetchBalance, upsertEntry],
  );

  const refreshBalance = useCallback(
    async (accountNumber: string) => ensureBalance(accountNumber, { force: true }),
    [ensureBalance],
  );

  useEffect(() => {
    if (!selectedAccounts || selectedAccounts.length === 0) {
      return;
    }

    selectedAccounts.forEach((account) => {
      const accountNumber = account.account["account-number"];
      const entry = entriesRef.current[accountNumber];
      if (!entry || !entry.balance) {
        void ensureBalance(accountNumber, { force: false });
      }
    });
  }, [selectedAccounts, ensureBalance]);

  const value = useMemo<AccountBalancesContextValue>(
    () => ({
      balances: balancesByAccount,
      getBalance: (accountNumber: string) =>
        balancesByAccount[accountNumber],
      ensureBalance,
      refreshBalance,
      isStreaming: streamerConnected,
      lastStreamUpdate,
    }),
    [
      balancesByAccount,
      ensureBalance,
      refreshBalance,
      streamerConnected,
      lastStreamUpdate,
    ],
  );

  return (
    <AccountBalancesContext.Provider value={value}>
      {children}
    </AccountBalancesContext.Provider>
  );
}

export function useAccountBalances() {
  const context = useContext(AccountBalancesContext);
  if (!context) {
    throw new Error(
      "useAccountBalances must be used within an AccountBalancesProvider",
    );
  }
  return context;
}
